import { useCallback, useEffect, useRef } from "react";
import { useToast } from "../components/ui/toast";

type DiscardAction = () => void | Promise<void>;

interface DiscardWarningOptions {
  dirty: boolean;
  discard: DiscardAction;
}

export function useDiscardWarning({ dirty, discard }: DiscardWarningOptions) {
  const toast = useToast();
  const armedUntil = useRef(0);

  const requestDiscard = useCallback(async (
    proceed: () => void,
    discardAction: DiscardAction = discard,
  ) => {
    if (!dirty) {
      proceed();
      return true;
    }

    const now = Date.now();
    if (now > armedUntil.current) {
      armedUntil.current = now + 5000;
      toast.warning(
        "Unsaved changes",
        "Repeat the action within 5 seconds to discard your changes.",
      );
      return false;
    }

    armedUntil.current = 0;
    try {
      await discardAction();
      proceed();
      return true;
    } catch {
      return false;
    }
  }, [dirty, discard, toast]);

  useEffect(() => {
    if (!dirty) armedUntil.current = 0;
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handleLinkClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target === "_blank" || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

      const destination = new URL(anchor.href, window.location.href);
      if (destination.origin !== window.location.origin) return;
      if (`${destination.pathname}${destination.search}${destination.hash}`
        === `${window.location.pathname}${window.location.search}${window.location.hash}`) return;

      event.preventDefault();
      event.stopPropagation();
      void requestDiscard(() => window.location.assign(destination.href));
    };
    document.addEventListener("click", handleLinkClick, true);
    return () => document.removeEventListener("click", handleLinkClick, true);
  }, [dirty, requestDiscard]);

  return requestDiscard;
}
